import { useState } from 'react';
import { Alert, Box, TextField, Typography } from '@mui/material';

// Remembered per browser: a convenience, not a record (wrapped, since storage can be blocked)
const load = (k: string, d: number) => {
  try { const v = Number(localStorage.getItem(k)); return Number.isFinite(v) && v > 0 ? v : d; } catch { return d; }
};
const save = (k: string, v: number) => { try { localStorage.setItem(k, String(v)); } catch { /* storage blocked */ } };
const money = (v: number) => `$${Math.round(v).toLocaleString()}`;

/**
 * How many shares to buy so that a fall to the tested 1-in-10 low costs no more than a set
 * share of the account. `low` is the bottom of the likely range (Feature 43 §14) or a stop.
 */
export default function PositionSizer({ price, low, lowLabel }: { price: number; low: number; lowLabel: string }) {
  const [account, setAccount] = useState(() => load('sizer.account', 100000));
  const [risk, setRisk] = useState(() => load('sizer.riskPct', 1));
  const perShare = price - low;
  const budget = account * risk / 100;
  const shares = perShare > 0 ? Math.floor(budget / perShare) : 0;
  const value = shares * price;
  const share = account > 0 ? value / account : 0;
  return (
    <Box sx={{ mt: 1.5, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Typography variant="subtitle2" gutterBottom>Size this position</Typography>
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 1 }}>
        <TextField size="small" label="Account ($)" type="number" value={account} sx={{ width: 150 }}
          onChange={e => { const v = Number(e.target.value); setAccount(v); save('sizer.account', v); }} />
        <TextField size="small" label="Risk per trade (%)" type="number" value={risk} sx={{ width: 150 }}
          inputProps={{ step: 0.25, min: 0.1, max: 10 }}
          onChange={e => { const v = Number(e.target.value); setRisk(v); save('sizer.riskPct', v); }} />
      </Box>
      {perShare <= 0 ? (
        <Typography variant="body2" color="text.secondary">The low is not below today's price.</Typography>
      ) : (
        <Typography variant="body2">
          Buy up to <strong>{shares.toLocaleString()} shares</strong> ({money(value)}, {(share * 100).toFixed(0)}% of the
          account). If the price falls to {lowLabel} (${low.toFixed(2)}, {((low / price - 1) * 100).toFixed(1)}%), that
          costs {money(shares * perShare)}, {risk}% of the account. It happens about 1 time in 10; a worse fall is possible.
        </Typography>
      )}
      {share > 0.25 && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          That is more than a quarter of the account in one stock. A narrow range makes the size large; the range is
          only right 8 times in 10.
        </Alert>
      )}
    </Box>
  );
}
